/**
 * Tab Management Handlers
 */

import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { buildAppUrl, documentUrlForPathOrId, findOpenTabForDocumentPath } from "@/lib/app-routes"
import { isDocumentDeepLinkRestorePending } from "@/lib/document-deep-link-pending"
import { useWorkspace } from "@/providers/workspace-provider"
import { toast } from "sonner"
import { markTabClickNavigation, markProgrammaticNavigation, getNavSerial } from "@/hooks/use-url-sync"
import { navDebug } from "@/lib/nav-debug"
import type { OpenTab } from "../../features/document-tabs"
import {
  fetchDocumentData,
  generateTabSlotId
} from "../resizable-layout.utils"
import type { DocumentData } from "../resizable-layout.types"
import type { usePerTabNavigationHistory } from "@/hooks/use-navigation-history"
import { isSoleHomeTab, pinHomeTabFirstInOpenTabs, planCloseManyTabs, trackDocumentView } from "./shared"
import { clearUserHiddenWorkspace } from "@/lib/user-hidden-workspaces"
import {
  isEditHistoryTabId,
  SEARCH_VIEW_TAB_ID,
  isSearchViewTabId,
} from "@/lib/static-app-views"
import { isModClick, resolveOpenIntent } from "@/lib/open-intent"
import { isPersonSurfaceTabId } from "@/lib/person-view"
import {
  platformSkillTabId,
  type OpenPlatformSkillDetail,
} from "@/lib/open-platform-skill"

export function useTabHandlers(
  openTabs: OpenTab[],
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  activeTabId: string | null,
  setActiveTabId: (id: string | null) => void,
  closedTabsHistory: OpenTab[],
  setClosedTabsHistory: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  setActiveDocumentPath: (path: string | null) => void,
  navigation: ReturnType<typeof usePerTabNavigationHistory>,
  navigate: ReturnType<typeof useNavigate>,
  getWorkspaceIdForPath?: (path: string) => string | undefined,
  activeWorkspaceIds?: string[],
  setActiveWorkspaceIds?: (ids: string[]) => void
) {
  const { workspaces } = useWorkspace()
  const workspacesRoute = useMemo(
    () => workspaces.map((w) => ({ id: w.id, slug: w.slug, contentRoot: w.contentRoot })),
    [workspaces]
  )

  /**
   * Switch to existing tab
   * Defined first to avoid circular dependency with createNewTab
   */
  const switchToExistingTab = useCallback(async (
    documentId: string,
    existingTab: OpenTab,
    options?: { fromUrlSync?: boolean },
  ) => {
    if (!options?.fromUrlSync) {
      markTabClickNavigation();
    }

    setActiveTabId(documentId);

    let documentPath = existingTab.filePath ?? (documentId.includes('/') ? documentId : null);
    let workspaceId = existingTab.workspaceId;

    // Re-verify the path for UUID-addressable tabs, even when we already have a cached
    // existingTab.filePath. The cache can go stale when the document was renamed/moved
    // externally (e.g. via an MCP tool from another session) while this tab stayed open -
    // there's no websocket/SSE push to refresh already-open tabs, so without this re-fetch
    // the click-through would keep navigating to the pre-rename URL even though the doc's
    // actual file_path (and the preview pane, which fetches fresh by ID) are already correct.
    const isSyntheticTab =
      existingTab.type === 'workspace-home' ||
      existingTab.type === 'desk-home' ||
      existingTab.type === 'edit-history' ||
      existingTab.type === 'search-view' ||
      existingTab.type === 'person-view' ||
      existingTab.type === 'home' ||
      existingTab.type === 'graph-view' ||
      existingTab.type === 'explorer' ||
      existingTab.type === 'inbox' ||
      existingTab.type === 'integration-google-drive' ||
      existingTab.type === 'integration-nextcloud' ||
      isEditHistoryTabId(documentId) ||
      isSearchViewTabId(documentId) ||
      (typeof documentId === 'string' &&
        (documentId.startsWith('workspace-home-') || documentId.startsWith('desk-home-')))
    if (documentId && !documentId.includes('/') && !isSyntheticTab) {
      try {
        const data = await fetchDocumentData(documentId, { workspaces: workspacesRoute });
        const p = data?.filePath ?? data?.path ?? null;
        if (p) documentPath = p;
      } catch {
        // Fetch failed - fall back to whatever path we already had (cached or null).
      }
    }

    // Always re-derive workspaceId from the authoritative documentPath when possible.
    // A cached existingTab.workspaceId can go stale (e.g. the document was deleted and
    // recreated under a different workspace after the tab was first opened) and must not
    // be trusted over a fresh path-based resolution — path-based resolution is also what
    // resolveTabWorkspaceId() prefers for tab-visibility filtering, so keeping this in sync
    // prevents a tab from surviving a workspace-scope switch it should have been hidden by.
    if (documentPath && getWorkspaceIdForPath) {
      const resolvedWorkspaceId = getWorkspaceIdForPath(documentPath);
      if (resolvedWorkspaceId != null) {
        workspaceId = resolvedWorkspaceId;
      }
    }

    const needsTabPatch =
      !!documentPath &&
      (existingTab.filePath !== documentPath ||
        (workspaceId != null && existingTab.workspaceId !== workspaceId));

    if (needsTabPatch) {
      setOpenTabs((prev) =>
        prev.map((tab) =>
          tab.id === documentId
            ? {
                ...tab,
                filePath: documentPath!,
                ...(workspaceId != null ? { workspaceId } : {}),
              }
            : tab
        )
      );
    }

    if (documentPath) {
      setActiveDocumentPath(documentPath);
      window.dispatchEvent(new CustomEvent('show-document-in-browse', { detail: { filePath: documentPath, documentId, forceRefresh: false } }));
    }

    const tabSlotId = existingTab.tabSlotId || `tab-${documentId}`;
    navigation.navigate(tabSlotId, { documentId, viewMode: 'single' }, "switch-existing-tab");
    if (existingTab.type === 'edit-history' || isEditHistoryTabId(documentId)) {
      markProgrammaticNavigation();
      navigate('/edit-history', { replace: true });
    }
    if (!isSyntheticTab) {
      await trackDocumentView(documentId, 'switch_tab', activeTabId);
    }
  }, [setActiveTabId, setActiveDocumentPath, setOpenTabs, navigation, activeTabId, getWorkspaceIdForPath, navigate]);

  /**
   * Create a new tab
   */
  const createNewTab = useCallback(async (
    documentId: string,
    prefetched?: DocumentData | null,
    options?: { reuseTabSlotId?: string; fromUrlSync?: boolean },
  ) => {
    if (!options?.fromUrlSync) {
      markTabClickNavigation();
    }
    navDebug.beginTabOpenTiming(documentId);

    let docData: DocumentData | null = prefetched ?? null;
    if (!docData) {
      navDebug.markTabFetchStart();
      try {
        docData = await fetchDocumentData(documentId, { workspaces: workspacesRoute });
      } catch (error) {
        console.error('[createNewTab] Failed to fetch document data:', error);
        throw error;
      } finally {
        navDebug.markTabFetchEnd();
      }
    }

    if (!docData) {
      throw new Error(`Document not found: ${documentId}`)
    }

    // Normalize document ID: always use UUID from API response if available
    // This prevents duplicate tabs when same document is opened via different identifiers (UUID vs file path)
    const normalizedDocumentId = docData.id || documentId;

    const documentPath = docData.filePath ?? docData.path ?? (documentId.includes('/') ? documentId : null)

    // Check if a tab with this normalized ID already exists
    const existingTabWithNormalizedId = openTabs.find(tab => tab.id === normalizedDocumentId);
    if (existingTabWithNormalizedId) {
      navDebug.cancelTabOpenTiming();
      await switchToExistingTab(normalizedDocumentId, existingTabWithNormalizedId, options);
      return;
    }

    // Path-keyed provisional / alias tab (cold deep link) - upgrade in place, never add a second tab
    const pathAliasTab =
      findOpenTabForDocumentPath(openTabs, documentId, workspacesRoute) ??
      (documentPath
        ? findOpenTabForDocumentPath(openTabs, documentPath, workspacesRoute)
        : undefined)
    if (pathAliasTab && pathAliasTab.id !== normalizedDocumentId) {
      navDebug.cancelTabOpenTiming();
      const workspaceId =
        (documentPath ? getWorkspaceIdForPath?.(documentPath) : undefined) ??
        docData.organizationId ??
        docData.organization_id
      const upgraded: OpenTab = {
        ...pathAliasTab,
        id: normalizedDocumentId,
        title: docData.title ?? pathAliasTab.title,
        type: docData.type ?? pathAliasTab.type ?? 'note',
        isDirty: false,
        ...(workspaceId != null && { workspaceId }),
        ...(documentPath != null && { filePath: documentPath }),
      }
      setOpenTabs((prev) => {
        const withoutAliases = prev.filter((tab) => {
          if (tab.id === pathAliasTab.id) return true
          if (tab.id === normalizedDocumentId) return false
          if (!documentPath) return true
          return !findOpenTabForDocumentPath([tab], documentPath, workspacesRoute)
        })
        return withoutAliases.map((tab) =>
          tab.id === pathAliasTab.id ? upgraded : tab,
        )
      })
      setActiveTabId(normalizedDocumentId)
      navDebug.markTabCreated(normalizedDocumentId)
      if (documentPath) {
        setActiveDocumentPath(documentPath)
        window.dispatchEvent(
          new CustomEvent('show-document-in-browse', {
            detail: { filePath: documentPath, documentId: normalizedDocumentId, forceRefresh: false },
          }),
        )
      }
      navigation.navigate(
        upgraded.tabSlotId || generateTabSlotId(),
        { documentId: normalizedDocumentId, viewMode: 'single' },
        'upgrade-path-alias-tab',
      )
      return
    }

    const newTabSlotId = options?.reuseTabSlotId || generateTabSlotId();

    const workspaceId =
      (documentPath ? getWorkspaceIdForPath?.(documentPath) : undefined) ??
      docData.organizationId ??
      docData.organization_id

    const newTab: OpenTab = {
      id: normalizedDocumentId, // Use normalized ID (UUID) instead of input identifier
      title: docData.title ?? `Document ${normalizedDocumentId.substring(0, 8)}...`,
      type: docData.type ?? 'note',
      isDirty: false,
      isPinned: false,
      tabSlotId: newTabSlotId,
      ...(workspaceId != null && { workspaceId }),
      ...(documentPath != null && { filePath: documentPath }),
    };

    // Cold deep-link session: keep the tab bar to this document only (no Home / LS restore).
    if (options?.fromUrlSync && isDocumentDeepLinkRestorePending()) {
      setOpenTabs([newTab])
    } else {
      setOpenTabs([...openTabs, newTab]);
    }
    setActiveTabId(normalizedDocumentId);
    navDebug.markTabCreated(normalizedDocumentId);

    // Set active document path if it's a file path (for tree navigation)
    if (documentPath) {
      setActiveDocumentPath(documentPath);
      // Avoid wiping the sidebar tree cache on every open — causes full "Loading…" flash. New files are picked up via content-revision / explorer refresh token.
      window.dispatchEvent(new CustomEvent('show-document-in-browse', { detail: { filePath: documentPath, documentId: normalizedDocumentId, forceRefresh: false } }));
    }

    // For new tabs opened from preview panes (like Basecamp todo work specs),
    // don't create navigation history - they should start fresh
    // Only create history if this is a deliberate navigation (not from preview)
    // We'll check if the current tab is an integration tab to determine this
    const currentTab = openTabs.find(tab => tab.id === activeTabId);
    const isFromIntegrationTab = currentTab?.type?.startsWith('integration-');

    if (isFromIntegrationTab) {
      // Opening from integration tab preview - start with empty history
      // Initialize history with just this entry (index 0, so back button is disabled)
      navigation.navigate(newTabSlotId, { documentId: normalizedDocumentId, viewMode: 'single' }, "create-new-tab-from-integration");
    } else {
      // Normal navigation - create history entry
      navigation.navigate(newTabSlotId, { documentId: normalizedDocumentId, viewMode: 'single' }, "create-new-tab");
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, setActiveDocumentPath, navigation, switchToExistingTab, getWorkspaceIdForPath, workspacesRoute]);

  /**
   * Replace current active tab
   */
  const replaceCurrentTab = useCallback(async (
    documentId: string,
    prefetched?: DocumentData | null,
    options?: { searchQuery?: string },
  ) => {
    const currentTab = openTabs.find(tab => tab.id === activeTabId);
    if (currentTab?.type === 'home' || activeTabId === 'home') {
      await createNewTab(documentId, prefetched);
      return;
    }
    // Workspace dashboard is a durable landing tab - never replace it with a document.
    if (
      currentTab?.type === 'workspace-home' ||
      currentTab?.type === 'desk-home' ||
      (typeof activeTabId === 'string' &&
        (activeTabId.startsWith('workspace-home-') || activeTabId.startsWith('desk-home-')))
    ) {
      await createNewTab(documentId, prefetched);
      return;
    }
    if (currentTab?.isPinned === true) {
      await createNewTab(documentId, prefetched);
      return;
    }

    markTabClickNavigation();
    navDebug.beginTabOpenTiming(documentId);

    let docData: DocumentData | null = prefetched ?? null;
    if (!docData) {
      navDebug.markTabFetchStart();
      try {
        docData = await fetchDocumentData(documentId, { workspaces: workspacesRoute });
      } finally {
        navDebug.markTabFetchEnd();
      }
    }

    if (!docData) {
      throw new Error(`Document not found: ${documentId}`)
    }

    // Normalize document ID: always use UUID from API response if available
    const normalizedDocumentId = docData.id || documentId;

    // Check if a tab with this normalized ID already exists (different from current tab)
    const existingTabWithNormalizedId = openTabs.find(tab =>
      tab.id === normalizedDocumentId && tab.id !== activeTabId
    );
    if (existingTabWithNormalizedId) {
      navDebug.cancelTabOpenTiming();
      await switchToExistingTab(normalizedDocumentId, existingTabWithNormalizedId);
      return;
    }

    const stableTabSlotId = currentTab?.tabSlotId === 'tab-main' ? 'tab-main' : (currentTab?.tabSlotId || 'tab-main');

    const documentPath = docData.filePath ?? docData.path ?? (documentId.includes('/') ? documentId : null)
    const workspaceId =
      (documentPath ? getWorkspaceIdForPath?.(documentPath) : undefined) ??
      docData.organizationId ??
      docData.organization_id

    const updatedTabs = openTabs.map(tab =>
      tab.id === activeTabId
        ? {
            id: normalizedDocumentId, // Use normalized ID (UUID) instead of input identifier
            title: docData.title ?? `Document ${normalizedDocumentId}`,
            type: docData.type ?? 'note',
            isDirty: false,
            isPinned: tab.isPinned ?? false, // Preserve pinned status
            tabSlotId: stableTabSlotId,
            ...(workspaceId != null && { workspaceId }),
            ...(documentPath != null && { filePath: documentPath }),
          }
        : tab
    );

    setOpenTabs(updatedTabs);
    setActiveTabId(normalizedDocumentId);
    navDebug.markTabCreated(normalizedDocumentId);

    // Set active document path if it's a file path (for tree navigation)
    if (documentPath) {
      setActiveDocumentPath(documentPath);
      // Avoid wiping the sidebar tree cache on every open — causes full "Loading…" flash. New files are picked up via content-revision / explorer refresh token.
      window.dispatchEvent(new CustomEvent('show-document-in-browse', { detail: { filePath: documentPath, documentId: normalizedDocumentId, forceRefresh: false } }));
    }

    // History must use the same tabSlotId as the tab row we just updated (see
    // NDJSON: replace-current-tab was writing tab-main while Explorer lived on
    // tab-1778..., splitting stacks and orphaning slots).
    const isFromPersonSurface =
      currentTab?.type === 'person-view' || isPersonSurfaceTabId(activeTabId)
    if (isFromPersonSurface && currentTab && !isPersonSurfaceTabId(normalizedDocumentId)) {
      const currentEntry = navigation.getCurrentEntry(stableTabSlotId)
      if (currentEntry?.documentId !== currentTab.id) {
        navigation.navigate(
          stableTabSlotId,
          {
            documentId: currentTab.id,
            viewMode: 'single',
            tabType: 'person-view',
            ...(currentTab.title ? { title: currentTab.title } : {}),
            ...(currentTab.workspaceId ? { organizationId: currentTab.workspaceId } : {}),
          },
          'replace-current-tab-preset-person',
        )
      }
    }

    const isFromSearchView = isSearchViewTabId(activeTabId) || currentTab?.type === 'search-view'
    if (isFromSearchView) {
      const searchQuery = options?.searchQuery ?? currentTab?.initialQuery ?? ''
      const searchPath = currentTab?.searchPath
      const currentEntry = navigation.getCurrentEntry(stableTabSlotId)
      if (!isSearchViewTabId(currentEntry?.documentId)) {
        navigation.navigate(
          stableTabSlotId,
          {
            documentId: SEARCH_VIEW_TAB_ID,
            viewMode: 'single',
            tabType: 'search-view',
            searchQuery,
            searchPath,
          },
          'replace-current-tab-preset-search',
        )
      }
    }

    navigation.navigate(stableTabSlotId, { documentId: normalizedDocumentId, viewMode: 'single' }, "replace-current-tab");
    await trackDocumentView(normalizedDocumentId, 'replace_tab', activeTabId);
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, setActiveDocumentPath, navigation, createNewTab, switchToExistingTab, getWorkspaceIdForPath, workspacesRoute]);

  /**
   * Handle document click
   */
  const handleDocumentClick = useCallback(async (
    documentId: string,
    event?: React.MouseEvent,
    options?: { searchQuery?: string },
  ) => {
    if (!documentId) {
      console.error('[handleDocumentClick] No documentId provided!')
      return
    }

    const currentTab = openTabs.find(tab => tab.id === activeTabId);

    const openIntent = resolveOpenIntent(event, { currentTab, activeTabId });
    const shouldOpenNewTab = openIntent === 'new';
    const openInNewTab = isModClick(event);

    // Snapshot nav serial before any async work. If the user presses back/forward
    // while fetchDocumentData is in flight, bumpNavSerial() will have changed it
    // and we bail out to avoid overwriting the restored navigation state.
    const capturedNavSerial = getNavSerial();

    // Normalize document ID early to check for existing tabs correctly
    // This prevents duplicate tabs when same document is opened via different identifiers (UUID vs file path)
    let normalizedDocumentId = documentId;
    let docDataForNormalization: DocumentData | null = null;
    try {
      // Quick fetch to normalize ID (only if not a special view)
      if (!isEditHistoryTabId(documentId) &&
          !documentId.startsWith('view:') &&
          !documentId.startsWith('basecamp-') &&
          !documentId.startsWith('integration-') &&
          documentId !== 'graph') {
        docDataForNormalization = await fetchDocumentData(documentId, { workspaces: workspacesRoute });
        if (docDataForNormalization?.id) {
          normalizedDocumentId = docDataForNormalization.id;
        }
      }
    } catch (error) {
      // If fetch fails, use original documentId
      console.warn('[handleDocumentClick] Failed to normalize document ID, using original:', error);
    }

    // Stale-fetch guard: if the user navigated away (back/forward) while we were
    // fetching, drop this click entirely to avoid replacing the restored tab.
    if (capturedNavSerial !== getNavSerial()) {
      console.log('[handleDocumentClick] Stale fetch detected (nav serial changed) — dropping navigation to', documentId);
      return;
    }

    // Auto-enable the workspace if this document belongs to one that is currently
    // disabled. Without this, useRecoverOrphanOrHiddenActiveTab will immediately
    // kick the user back to a visible tab after opening from search/chat.
    if (getWorkspaceIdForPath && activeWorkspaceIds && setActiveWorkspaceIds) {
      const docPath = docDataForNormalization?.filePath ?? docDataForNormalization?.path ?? (documentId.includes('/') ? documentId : null)
      const wsId =
        (docPath ? getWorkspaceIdForPath(docPath) : undefined) ??
        docDataForNormalization?.organizationId ??
        docDataForNormalization?.organization_id
      if (wsId && !activeWorkspaceIds.includes(wsId)) {
        // Cmd+P / tree click is an explicit open. Clear the hide mark, then enable.
        clearUserHiddenWorkspace(wsId)
        console.log('[handleDocumentClick] Auto-enabling workspace for document:', wsId)
        setActiveWorkspaceIds([...activeWorkspaceIds, wsId])
      }
    }

    // Check for existing tab using normalized ID
    const existingTab = openTabs.find(tab => tab.id === normalizedDocumentId || tab.id === documentId);
    const isFromIntegrationTab = currentTab?.type?.startsWith('integration-');
    const isGraphTabClick = documentId === 'graph';
    const isGraphTab = activeTabId === 'graph' || currentTab?.type === 'graph-view';
    const isCurrentTabPinned = currentTab?.isPinned === true;
    const isFromHomeTab = currentTab?.type === 'home' || activeTabId === 'home';

    if (isEditHistoryTabId(documentId)) {
      // Turfed: Activity / All edits is Search ?view=activity
      window.dispatchEvent(
        new CustomEvent('flownotes:open-search-view', {
          detail: { query: '', view: 'activity' },
        }),
      );
      return;
    }

    // Handle graph tab specially
    if (isGraphTabClick) {
      if (openInNewTab) {
        if (existingTab) {
          await switchToExistingTab(documentId, existingTab);
        } else {
          markTabClickNavigation();
          const newTabSlotId = generateTabSlotId();
          const newTab: OpenTab = {
            id: 'graph',
            title: 'Graph View',
            type: 'graph-view',
            isDirty: false,
            isPinned: false,
            tabSlotId: newTabSlotId,
          };
          setOpenTabs([...openTabs, newTab]);
          setActiveTabId('graph');
          navigation.navigate(newTabSlotId, { documentId: 'graph', viewMode: 'single' }, "graph-tab-new");
        }
      } else if (existingTab) {
        await switchToExistingTab(documentId, existingTab);
      } else if (shouldOpenNewTab) {
        markTabClickNavigation();
        const newTabSlotId = generateTabSlotId();
        const newTab: OpenTab = {
          id: 'graph',
          title: 'Graph View',
          type: 'graph-view',
          isDirty: false,
          tabSlotId: newTabSlotId,
        };
        setOpenTabs([...openTabs, newTab]);
        setActiveTabId('graph');
        navigation.navigate(newTabSlotId, { documentId: 'graph', viewMode: 'single' }, "graph-tab-new-from-pinned-or-integration");
      } else {
        markTabClickNavigation();
        const graphCurrentTab = openTabs.find(tab => tab.id === activeTabId);
        const stableTabSlotId = graphCurrentTab?.tabSlotId || generateTabSlotId();
        const updatedTabs = openTabs.map(tab =>
          tab.id === activeTabId
            ? {
                id: 'graph',
                title: 'Graph View',
                type: 'graph-view' as const,
                isDirty: false,
                tabSlotId: stableTabSlotId,
              }
            : tab
        );
        setOpenTabs(updatedTabs);
        setActiveTabId('graph');
        navigation.navigate(stableTabSlotId, { documentId: 'graph', viewMode: 'single' }, "graph-tab-replace-current");
      }
      return;
    }

    // Decision logic for tab creation/replacement
    try {
      if (openInNewTab || isGraphTab) {
        if (existingTab) {
          await switchToExistingTab(existingTab.id, existingTab);
        } else {
          await createNewTab(documentId, docDataForNormalization);
        }
      } else if (existingTab) {
        await switchToExistingTab(existingTab.id, existingTab);
      } else if (shouldOpenNewTab) {
        // Home reuses its slot for history continuity. Workspace-home keeps its own tab
        // and always gets a fresh slot so the dashboard stays open beside the document.
        await createNewTab(
          documentId,
          docDataForNormalization,
          isFromHomeTab ? { reuseTabSlotId: currentTab?.tabSlotId || 'tab-main' } : undefined,
        );
      } else {
        await replaceCurrentTab(documentId, docDataForNormalization, options);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      const notFound = /document not found/i.test(message)
      if (notFound) {
        const label =
          documentId.length > 64 ? `${documentId.slice(0, 61)}...` : documentId
        console.warn('[handleDocumentClick] Document not found:', documentId)
        toast.error('Document not found', {
          description: `Could not open "${label}".`,
        })
        return
      }
      console.error('[handleDocumentClick] Error during document navigation:', error)
      throw error
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, navigation, createNewTab, replaceCurrentTab, switchToExistingTab, getWorkspaceIdForPath, activeWorkspaceIds, setActiveWorkspaceIds]);

  /**
   * Handle document close
   */
  const handleDocumentClose = useCallback((documentId: string) => {
    const closedTab = openTabs.find(doc => doc.id === documentId);
    if (closedTab && isSoleHomeTab(openTabs, documentId)) {
      return
    }
    if (closedTab) {
      setClosedTabsHistory(prev => {
        const updated = [closedTab, ...prev.filter(tab => tab.id !== documentId)];
        return updated.slice(0, 10);
      });
    }

    const newOpenTabs = pinHomeTabFirstInOpenTabs(openTabs.filter(doc => doc.id !== documentId));
    setOpenTabs(newOpenTabs);

    if (documentId === activeTabId) {
      if (newOpenTabs.length > 0) {
        const homeTab = newOpenTabs.find((t) => t.id === 'home' || t.type === 'home')
        const isPseudoUtilityTab = (t: OpenTab) =>
          isEditHistoryTabId(t.id) || t.id.startsWith('view:') || t.type === 'search-view' || t.type === 'person-view'
        const eligibleTabs = newOpenTabs.filter(
          (t) => t.type !== 'inbox-thread' && !isPseudoUtilityTab(t),
        )
        const newActiveTab =
          homeTab ??
          (eligibleTabs.length > 0
            ? eligibleTabs[eligibleTabs.length - 1]
            : newOpenTabs[newOpenTabs.length - 1])
        setActiveTabId(newActiveTab.id)
        markProgrammaticNavigation()
        navigate(buildAppUrl({ tab: newActiveTab, workspaces: workspacesRoute }), {
          replace: true,
        })
      } else {
        const homeTab: OpenTab = {
          id: 'home',
          title: 'Home',
          type: 'home' as const,
          isDirty: false,
          isPinned: false,
          tabSlotId: 'tab-main',
        };
        setOpenTabs([homeTab]);
        setActiveTabId('home');
        markProgrammaticNavigation();
        navigation.navigate('tab-main', { documentId: null, viewMode: 'list' }, "tab-handlers-close-no-tabs");
        navigate('/', { replace: true });
      }
    }

    if (closedTab) {
      const label = closedTab.title || 'Tab';
      toast(`Closed "${label}"`, {
        action: {
          label: 'Undo',
          onClick: () => {
            setClosedTabsHistory(prev => prev.filter(t => t.id !== closedTab.id));
            setOpenTabs(prev => {
              if (prev.find(t => t.id === closedTab.id)) return prev;
              return [...prev, { ...closedTab, isDirty: false }];
            });
            setActiveTabId(closedTab.id);
            if (closedTab.tabSlotId) {
              navigation.navigate(closedTab.tabSlotId, {
                documentId: closedTab.id,
                viewMode: 'single'
              }, "undo-close-tab");
            }
            markProgrammaticNavigation();
            navigate(buildAppUrl({ tab: closedTab, workspaces: workspacesRoute }), {
              replace: true,
            });
          },
        },
        duration: 5000,
      });
    }
  }, [
    openTabs,
    activeTabId,
    setOpenTabs,
    setActiveTabId,
    setClosedTabsHistory,
    navigation,
    navigate,
    workspacesRoute,
  ]);

  const handleCloseManyDocuments = useCallback((documentIds: string[]) => {
    const { remaining, closed, nextActiveId } = planCloseManyTabs(
      openTabs,
      documentIds,
      activeTabId,
    )
    if (closed.length === 0) return

    setClosedTabsHistory((prev) => {
      const updated = [...closed, ...prev.filter((tab) => !closed.some((c) => c.id === tab.id))]
      return updated.slice(0, 10)
    })
    setOpenTabs(remaining)

    if (nextActiveId && nextActiveId !== activeTabId) {
      const nextTab = remaining.find((tab) => tab.id === nextActiveId)
      if (nextTab) {
        setActiveTabId(nextTab.id)
        markProgrammaticNavigation()
        navigate(buildAppUrl({ tab: nextTab, workspaces: workspacesRoute }), { replace: true })
      }
    }
  }, [
    openTabs,
    activeTabId,
    setClosedTabsHistory,
    setOpenTabs,
    setActiveTabId,
    navigate,
    workspacesRoute,
  ])

  /**
   * Reopen last closed tab
   */
  const handleReopenLastClosedTab = useCallback(async () => {
    if (closedTabsHistory.length === 0) {
      return;
    }

    const lastClosedTab = closedTabsHistory[0];

    setClosedTabsHistory(prev => prev.slice(1));

    const existingTab = openTabs.find(tab => tab.id === lastClosedTab.id);
    if (existingTab) {
      setActiveTabId(lastClosedTab.id);
      if (lastClosedTab.id.includes('/')) {
        setActiveDocumentPath(lastClosedTab.id);
      }
    } else {
      const newTab: OpenTab = {
        ...lastClosedTab,
        isDirty: false,
      };
      setOpenTabs([...openTabs, newTab]);
      setActiveTabId(lastClosedTab.id);
      if (lastClosedTab.id.includes('/')) {
        setActiveDocumentPath(lastClosedTab.id);
      }

      if (lastClosedTab.tabSlotId) {
        navigation.navigate(lastClosedTab.tabSlotId, {
          documentId: lastClosedTab.id,
          viewMode: 'single'
        }, "reopen-last-closed-tab");
      }

      await trackDocumentView(lastClosedTab.id, 'reopen_tab', activeTabId);
    }
  }, [closedTabsHistory, openTabs, setOpenTabs, setActiveTabId, setActiveDocumentPath, setClosedTabsHistory, navigation, activeTabId]);

  /**
   * Handle document reorder
   */
  const handleDocumentReorder = useCallback((reorderedDocuments: typeof openTabs) => {
    setOpenTabs(reorderedDocuments);
  }, [setOpenTabs]);

  const openDrivePreviewTab = useCallback(
    (
      file: {
        id: string
        name: string
        mimeType: string
        webViewLink?: string
        shortcutTarget?: { id: string; mimeType: string } | null
      },
      options: {
        organizationId: string
        event?: React.MouseEvent
        importedDocumentId?: string
      },
    ) => {
      // Prevent URL sync from reverting activeTabId before the address bar updates
      markTabClickNavigation()

      const tabId = `gdrive-preview-${file.id}`
      const existing = openTabs.find((t) => t.id === tabId)
      const intent = resolveOpenIntent(options.event, {
        currentTab: openTabs.find((t) => t.id === activeTabId) ?? null,
        activeTabId,
      })

      // For shortcuts, resolve to the target so preview/import uses the real file
      const previewFileId = file.shortcutTarget?.id ?? file.id
      const previewMimeType = file.shortcutTarget?.mimeType ?? file.mimeType

      const newTab: OpenTab = {
        id: tabId,
        title: file.name,
        type: 'integration-google-drive-preview',
        isDirty: false,
        workspaceId: options.organizationId,
        integrationData: {
          integrationId: 'google-drive',
          organizationId: options.organizationId,
          driveFileId: previewFileId,
          driveMimeType: previewMimeType,
          driveWebViewLink: file.webViewLink,
          importedDocumentId: options.importedDocumentId,
          driveIsShortcut: file.shortcutTarget != null,
          driveShortcutSourceId: file.shortcutTarget != null ? file.id : undefined,
        },
      }

      if (existing) {
        const slot = existing.tabSlotId ?? generateTabSlotId()
        if (intent === 'replace') {
          setOpenTabs((prev) => prev.map((t) => (t.id === tabId ? { ...newTab, tabSlotId: slot } : t)))
        }
        setActiveTabId(tabId)
        navigation.navigate(slot, { documentId: tabId, viewMode: 'single' }, 'open-drive-preview-existing')
        return
      }

      if (intent === 'replace' && activeTabId) {
        const slot = openTabs.find((t) => t.id === activeTabId)?.tabSlotId ?? generateTabSlotId()
        setOpenTabs((prev) =>
          prev.map((t) =>
            t.id === activeTabId ? { ...newTab, tabSlotId: slot } : t,
          ),
        )
        setActiveTabId(tabId)
        navigation.navigate(slot, { documentId: tabId, viewMode: 'single' }, 'open-drive-preview')
      } else {
        const tabSlotId = generateTabSlotId()
        setOpenTabs((prev) => [...prev, { ...newTab, tabSlotId }])
        setActiveTabId(tabId)
        navigation.navigate(tabSlotId, { documentId: tabId, viewMode: 'single' }, 'open-drive-preview')
      }
    },
    [openTabs, activeTabId, setOpenTabs, setActiveTabId, navigation],
  )

  const openPlatformSkillTab = useCallback(
    (skill: OpenPlatformSkillDetail) => {
      markTabClickNavigation()
      const tabId = platformSkillTabId(skill.slug)
      const existing = openTabs.find((t) => t.id === tabId)
      const newTab: OpenTab = {
        id: tabId,
        title: skill.name || skill.slug,
        type: 'platform-skill',
        isDirty: false,
      }

      if (existing) {
        setActiveTabId(tabId)
        const slot = existing.tabSlotId ?? generateTabSlotId()
        navigation.navigate(slot, { documentId: tabId, viewMode: 'single' }, 'open-platform-skill-existing')
        return
      }

      const tabSlotId = generateTabSlotId()
      setOpenTabs((prev) => [...prev, { ...newTab, tabSlotId }])
      setActiveTabId(tabId)
      navigation.navigate(tabSlotId, { documentId: tabId, viewMode: 'single' }, 'open-platform-skill')
    },
    [openTabs, setOpenTabs, setActiveTabId, navigation],
  )

  return {
    createNewTab,
    replaceCurrentTab,
    switchToExistingTab,
    handleDocumentClick,
    handleDocumentClose,
    handleCloseManyDocuments,
    handleReopenLastClosedTab,
    handleDocumentReorder,
    openDrivePreviewTab,
    openPlatformSkillTab,
  };
}
